import React, {Component, createRef, ReactNode, RefObject} from 'react';

import {KeyboardActionsEnum} from '../../../../../../libs/shared/core/src/helpers/browser/keyboardHelpers';
import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {
  ClickEventHandler,
  EventHandler,
  FrontClickEvent,
} from '../../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {
  SendActionsEnum,
  SendMessageOptionsModel,
} from '../../../../../../libs/shared/core/src/models/api/messageModel';
import {PaletteColorsEnum} from '../../../../../../libs/shared/core/src/models/api/paletteModel';
import {
  InterfaceEventsEnum,
  trackInterfaceEvent,
} from '../../../../../../libs/shared/core/src/services/tracking/tracking';
import {ButtonVariantsEnum} from '../../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {DropdownInheritableProps} from '../../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownItem} from '../../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItem';
import {DropdownItemChevron} from '../../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemChevron';
import {DropdownItemContent} from '../../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemContent';
import {DropdownItemIcon} from '../../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemIcon';
import {SimpleDropdown} from '../../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown/simpleDropdown';
import {Icon} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {KeyboardShortcuts} from '../../../../../../libs/shared/ui/src/components/keyboard/keyboardShortcuts';
import {startSimplePerformanceInteraction} from '../../../../../../srcCommon/services/performanceMetrics/interactionPerformance';
import {PerformanceInteractionTypesEnum} from '../../../../../../srcCommon/services/performanceMetrics/interactionPerformanceTypes';
import {PermissionResult} from '../../../../../helpers/permissionHelpers';
import {ConversationModel} from '../../../../../models/api/conversationModel';
import {DefaultSendActionsEnum} from '../../../../../models/api/preferencesModel';
import {PermissionDropdownItem} from '../../../../common/dropdowns/permissionDropdownItem';
import {SafetyNetActionsEnum} from '../../../viewer/conversation/safetyNets/safetyNetsContext';
import {SafetyNetsCoordinator} from '../../../viewer/conversation/safetyNets/safetyNetsCoordinator';
import {ReminderDropdown} from '../dropdowns/reminderDropdown';
import {SendLaterDropdown} from '../dropdowns/sendLaterDropdown';
import {SendToolbarButtonBase} from './sendToolbarButtonBase';

/*
 * Props.
 */

export type SendMessage = (options?: Partial<SendMessageOptionsModel>) => void;

interface SendToolbarButtonProps {
  isDisabled: boolean;
  renderTooltip: () => ReactNode;
  areSendShortcutsDisabled?: boolean;
  hasClosePermission: boolean;
  updateConversationStatusOfflinePermission: PermissionResult;
  defaultSendAction: DefaultSendActionsEnum;
  sendMessage: SendMessage;
  conversation: ConversationModel | null;
  isLabelHidden?: boolean;
}

/*
 * Component.
 */

interface SendToolbarButtonState {
  activeDropdownAnchor?: SendToolbarButtonDropdownAnchorsEnum;
  activeDropdownItem?: SendToolbarButtonDropdownItemsEnum;
}

enum SendToolbarButtonDropdownAnchorsEnum {
  /** Dropdown is opened from the main part of the button. */
  DEFAULT = 'DEFAULT',
  /** Dropdown is opened from the chevron part of the button. */
  OPTIONS = 'OPTIONS',
}

enum SendToolbarButtonDropdownItemsEnum {
  /** Later time subdropdown is open. */
  SEND_LATER = 'SEND_LATER',
  /** Snooze time subdropdown is open. */
  SEND_AND_SNOOZE = 'SEND_AND_SNOOZE',
}

export class SendToolbarButton extends Component<SendToolbarButtonProps, SendToolbarButtonState> {
  constructor(props: SendToolbarButtonProps) {
    super(props);

    this.state = {};
    this.chromeRef = createRef<HTMLDivElement>();
  }

  private chromeRef: RefObject<HTMLDivElement>;

  private closeDropdown() {
    this.setState({activeDropdownAnchor: undefined, activeDropdownItem: undefined});
  }

  /*
   * Event handlers.
   */

  private readonly onDefaultButtonClick: ClickEventHandler = (event) => {
    startSimplePerformanceInteraction(event, PerformanceInteractionTypesEnum.SEND_MESSAGE);

    switch (this.props.defaultSendAction) {
      case DefaultSendActionsEnum.SEND:
        this.onSendClick();
        break;

      case DefaultSendActionsEnum.SEND_AND_ARCHIVE:
        this.onSendAndArchiveClick();
        break;

      case DefaultSendActionsEnum.SEND_AND_SNOOZE:
        this.onSendAndSnoozeClick();
        break;

      default:
    }
  };

  private readonly onOptionsButtonClick: () => void = () => {
    this.setState({
      activeDropdownAnchor: SendToolbarButtonDropdownAnchorsEnum.OPTIONS,
      activeDropdownItem: undefined,
    });

    trackInterfaceEvent(InterfaceEventsEnum.OPEN_MESSAGE_SEND_OPTIONS);
  };

  private readonly onDropdownRequestClose: () => void = () => {
    this.closeDropdown();
  };

  private readonly onSendLaterItemSelect: EventHandler = (event) => {
    this.setState({activeDropdownItem: SendToolbarButtonDropdownItemsEnum.SEND_LATER});
    event.preventDefault();
  };

  private readonly onSendAndSnoozeItemSelect: EventHandler = (event) => {
    this.setState({activeDropdownItem: SendToolbarButtonDropdownItemsEnum.SEND_AND_SNOOZE});
    event.preventDefault();
  };

  private readonly onSendClick = () => {
    this.props.sendMessage({sendAction: SendActionsEnum.SEND});
  };

  private readonly onSendAndArchiveClick = () => {
    this.props.sendMessage({sendAction: SendActionsEnum.SEND_AND_ARCHIVE});
  };

  private readonly onSendAndSnoozeClick = () => {
    const {defaultSendAction} = this.props;
    const activeDropdownAnchor =
      defaultSendAction === DefaultSendActionsEnum.SEND_AND_SNOOZE
        ? SendToolbarButtonDropdownAnchorsEnum.DEFAULT
        : SendToolbarButtonDropdownAnchorsEnum.OPTIONS;

    this.setState({
      activeDropdownAnchor,
      activeDropdownItem: SendToolbarButtonDropdownItemsEnum.SEND_AND_SNOOZE,
    });
  };

  private readonly onSendLaterTimeSelect: (arg: number) => void = (sendLaterAt) => {
    this.closeDropdown();
    this.props.sendMessage({sendLaterAt});

    // Tracking.
    const delay = Math.round((sendLaterAt - Date.now()) / 1000);
    trackInterfaceEvent(InterfaceEventsEnum.MESSAGE_SENT_LATER, {delay});
  };

  private readonly onSendAndSnoozeTimeSelect: (arg: number) => void = (remindAt) => {
    this.closeDropdown();
    this.props.sendMessage({remindAt, sendAction: SendActionsEnum.SEND_AND_ARCHIVE});
  };

  /*
   * Render.
   */

  render() {
    const {isDisabled, isLabelHidden, renderTooltip, defaultSendAction, areSendShortcutsDisabled} =
      this.props;

    const keyHandlers = {
      [KeyboardActionsEnum.SEND]: this.onSendClick,
      [KeyboardActionsEnum.SEND_AND_SNOOZE]: this.onSendAndSnoozeClick,
    };

    const isButtonSendAndSnooze = defaultSendAction === DefaultSendActionsEnum.SEND_AND_SNOOZE;
    const variant = isButtonSendAndSnooze ? ButtonVariantsEnum.CUSTOM_DISABLED : ButtonVariantsEnum.PRIMARY;
    const color = isButtonSendAndSnooze ? PaletteColorsEnum.ORANGE : undefined;

    const {activeDropdownAnchor} = this.state;

    return (
      <SafetyNetsCoordinator
        conversations={this.props.conversation ? [this.props.conversation] : []}
        safetyNetActionType={SafetyNetActionsEnum.SEND_AND_ARCHIVE}
        onActionValidated={this.onSendAndArchiveClick}
      >
        {(onSendAndArchiveClick) => (
          <KeyboardShortcuts
            handlers={{...keyHandlers, [KeyboardActionsEnum.SEND_AND_DONE]: onSendAndArchiveClick}}
            isDisabled={isDisabled || areSendShortcutsDisabled}
          >
            <SafetyNetsCoordinator
              conversations={this.props.conversation ? [this.props.conversation] : []}
              onActionValidated={(event: FrontClickEvent) => this.onDefaultButtonClick(event)}
              safetyNetActionType={getSafetyNetActionFromDefaultSendAction(this.props.defaultSendAction)}
            >
              {(onDefaultButtonClick) => (
                <SendToolbarButtonBase
                  chromeRef={this.chromeRef}
                  variant={variant}
                  color={color}
                  isDisabled={isDisabled}
                  renderTooltip={renderTooltip}
                  isDefaultDropdownOpen={
                    activeDropdownAnchor === SendToolbarButtonDropdownAnchorsEnum.DEFAULT
                  }
                  onDefaultButtonClick={(event) => onDefaultButtonClick(event)}
                  renderDefaultButtonContent={() => this.renderDefaultButtonContent()}
                  renderAbbreviatedDefaultButtonContent={
                    isLabelHidden ? (buttonProps) => renderSendIcon(buttonProps.isDisabled) : undefined
                  }
                  isOptionsDropdownOpen={
                    activeDropdownAnchor === SendToolbarButtonDropdownAnchorsEnum.OPTIONS
                  }
                  onOptionsButtonClick={() => this.onOptionsButtonClick()}
                />
              )}
            </SafetyNetsCoordinator>
            {this.maybeRenderDropdown()}
          </KeyboardShortcuts>
        )}
      </SafetyNetsCoordinator>
    );
  }

  private renderDefaultButtonContent() {
    switch (this.props.defaultSendAction) {
      case DefaultSendActionsEnum.SEND:
        return sendLabel;

      case DefaultSendActionsEnum.SEND_AND_ARCHIVE:
        return sendAndArchiveLabel;

      case DefaultSendActionsEnum.SEND_AND_SNOOZE:
        return sendAndSnoozeLabel;

      default:
        return null;
    }
  }

  /** Render a dropdown for secondary send actions. */
  private maybeRenderDropdown() {
    const {activeDropdownAnchor, activeDropdownItem} = this.state;
    if (!activeDropdownAnchor) {
      return null;
    }

    const dropdownProps: DropdownInheritableProps = {
      anchor: this.chromeRef,
      onRequestClose: this.onDropdownRequestClose,
      placement: 'top-end',
    };

    switch (activeDropdownItem) {
      case SendToolbarButtonDropdownItemsEnum.SEND_LATER:
        return this.renderSendLaterDropdown(dropdownProps);

      case SendToolbarButtonDropdownItemsEnum.SEND_AND_SNOOZE:
        return this.renderSendAndSnoozeDropdown(dropdownProps);

      default:
        return this.renderOptionsDropdown(dropdownProps);
    }
  }

  private renderSendLaterDropdown(dropdownProps: DropdownInheritableProps) {
    return (
      <SendLaterDropdown {...dropdownProps} onValueSelect={(sendAt) => this.onSendLaterTimeSelect(sendAt)} />
    );
  }

  private renderSendAndSnoozeDropdown(dropdownProps: DropdownInheritableProps) {
    return (
      <ReminderDropdown
        {...dropdownProps}
        onValueSelect={(snoozeAt) => this.onSendAndSnoozeTimeSelect(snoozeAt)}
      />
    );
  }

  private renderOptionsDropdown(dropdownProps: DropdownInheritableProps) {
    const {hasClosePermission, updateConversationStatusOfflinePermission} = this.props;

    return (
      <SimpleDropdown {...dropdownProps}>
        <DropdownItem onSelect={this.onSendClick}>
          <DropdownItemIcon iconName="sendSmall" iconSize={16} label={sendLabel} />
          <DropdownItemContent>{sendLabel}</DropdownItemContent>
        </DropdownItem>
        <SafetyNetsCoordinator
          conversations={this.props.conversation ? [this.props.conversation] : []}
          onActionValidated={() => this.onSendAndArchiveClick()}
          safetyNetActionType={SafetyNetActionsEnum.SEND_AND_ARCHIVE}
        >
          {(onSendAndArchiveClick) => (
            <PermissionDropdownItem
              offlinePermission={updateConversationStatusOfflinePermission}
              onSelect={onSendAndArchiveClick}
              isDisabled={!hasClosePermission}
            >
              <DropdownItemIcon iconName="archiveSmall" iconSize={16} label={sendLaterLabel} />
              <DropdownItemContent>{sendAndArchiveLabel}</DropdownItemContent>
            </PermissionDropdownItem>
          )}
        </SafetyNetsCoordinator>
        <PermissionDropdownItem onSelect={this.onSendLaterItemSelect}>
          <DropdownItemIcon iconName="timeSmall" iconSize={16} label={sendLaterLabel} />
          <DropdownItemContent>{sendLaterLabel}</DropdownItemContent>
          <DropdownItemChevron />
        </PermissionDropdownItem>
        <PermissionDropdownItem
          offlinePermission={updateConversationStatusOfflinePermission}
          onSelect={this.onSendAndSnoozeItemSelect}
        >
          <DropdownItemIcon iconName="snoozeSmall" iconSize={16} label={sendAndSnoozeLabel} />
          <DropdownItemContent>{sendAndSnoozeLabel}</DropdownItemContent>
          <DropdownItemChevron />
        </PermissionDropdownItem>
      </SimpleDropdown>
    );
  }
}

const sendLabel = <Trans id="composer-send">Send</Trans>;
const sendAndArchiveLabel = <Trans id="composer-send-archive">Send &amp; Archive</Trans>;
const sendLaterLabel = <Trans id="composer-send-later">Send Later</Trans>;
const sendAndSnoozeLabel = <Trans id="composer-send-snooze">Send &amp; Snooze</Trans>;

function renderSendIcon(isDisabled?: boolean) {
  return <Icon name="sendFilledSmall" isDisabled={isDisabled} />;
}

const defaultSendActionSafetyNetActionMap: Record<DefaultSendActionsEnum, SafetyNetActionsEnum> = {
  [DefaultSendActionsEnum.SEND]: SafetyNetActionsEnum.SEND,
  [DefaultSendActionsEnum.SEND_AND_ARCHIVE]: SafetyNetActionsEnum.SEND_AND_ARCHIVE,
  [DefaultSendActionsEnum.SEND_AND_SNOOZE]: SafetyNetActionsEnum.SEND_AND_SNOOZE,
};

function getSafetyNetActionFromDefaultSendAction(
  defaultSendAction: DefaultSendActionsEnum,
): SafetyNetActionsEnum {
  return defaultSendActionSafetyNetActionMap[defaultSendAction];
}
